import { useEffect, useState } from "react";
import {
  Circle, CircleDashed, Timer, Eye, Rocket, LineChart, CheckCircle2, XCircle,
  ArrowUp, ArrowRight, ArrowDown, AlertOctagon,
} from "lucide-react";
import * as api from "../../lib/api";

export const PRIMARY = "#018D4C";

export const STATUS_META = {
  todo:        { label: "To do",       icon: Circle,       color: "#71717a" },
  in_progress: { label: "In progress", icon: Timer,        color: "#2563EB" },
  in_review:   { label: "In review",   icon: Eye,          color: "#7C3AED" },
  shipped:     { label: "Shipped",     icon: Rocket,       color: "#0D9488" },
  measuring:   { label: "Measuring",   icon: LineChart,    color: "#D97706" },
  done:        { label: "Done",        icon: CheckCircle2, color: PRIMARY },
  canceled:    { label: "Canceled",    icon: XCircle,      color: "#a1a1aa" },
};
export const OPEN_STATUSES = ["todo", "in_progress", "in_review", "shipped", "measuring"];

export const PRIORITY_META = {
  urgent: { label: "Urgent", icon: AlertOctagon, color: "#DC2626" },
  high:   { label: "High",   icon: ArrowUp,      color: "#EA580C" },
  medium: { label: "Medium", icon: ArrowRight,   color: "#71717a" },
  low:    { label: "Low",    icon: ArrowDown,    color: "#a1a1aa" },
};

export const ACTION_META = {
  write_new:           { label: "Write new",       bg: "#ECFDF5", text: "#065F46" },
  optimize:            { label: "Optimize",        bg: "#FFFBEB", text: "#92400E" },
  fix_cannibalization: { label: "Cannibalization", bg: "#FFF1F2", text: "#BE123C" },
  internal_links:      { label: "Internal links",  bg: "#EFF6FF", text: "#1D4ED8" },
  technical:           { label: "Technical",       bg: "#F4F4F5", text: "#52525B" },
  other:               { label: "Other",           bg: "#F4F4F5", text: "#52525B" },
};

export function StatusLabel({ status, className = "" }) {
  const m = STATUS_META[status] || STATUS_META.todo;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs ${className}`}>
      <Icon size={13} style={{ color: m.color }} />
      <span className="text-zinc-700">{m.label}</span>
    </span>
  );
}

export function PriorityLabel({ priority, className = "" }) {
  const m = PRIORITY_META[priority] || PRIORITY_META.medium;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs ${className}`}>
      <Icon size={13} style={{ color: m.color }} />
      <span className="text-zinc-700">{m.label}</span>
    </span>
  );
}

export function ActionBadge({ action }) {
  const m = ACTION_META[action] || ACTION_META.other;
  return (
    <span
      className="px-1.5 py-0.5 text-[9px] tracking-[0.08em] font-bold uppercase rounded whitespace-nowrap"
      style={{ background: m.bg, color: m.text }}
    >
      {m.label}
    </span>
  );
}

// Small task chip used in Search Performance rows: "SEO-42 · In progress".
export function TaskChip({ number, status }) {
  const m = STATUS_META[status] || STATUS_META.todo;
  const Icon = m.icon;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-px rounded border border-zinc-200 bg-white text-[10px] text-zinc-600 whitespace-nowrap">
      <Icon size={10} style={{ color: m.color }} />
      <span className="font-bold">SEO-{number}</span>
      <span className="text-zinc-400">· {m.label}</span>
    </span>
  );
}

function initials(name) {
  return (name || "?").split(/\s+/).map(p => p[0]).join("").slice(0, 2).toUpperCase();
}

export function MemberAvatar({ member, size = 20 }) {
  if (!member) {
    return (
      <span
        className="inline-flex items-center justify-center rounded-full border border-dashed border-zinc-300 text-zinc-300"
        style={{ width: size, height: size }}
        title="Unassigned"
      />
    );
  }
  return member.image_url ? (
    <img
      src={member.image_url}
      alt={member.name}
      title={member.name}
      className="rounded-full object-cover"
      style={{ width: size, height: size }}
    />
  ) : (
    <span
      className="inline-flex items-center justify-center rounded-full bg-zinc-200 text-zinc-600 font-bold"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
      title={member.name}
    >
      {initials(member.name)}
    </span>
  );
}

// Org members (Clerk, via the backend) + whether the caller may edit tasks (clients can't).
export function useTaskMembers() {
  const [members, setMembers] = useState([]);
  const [canEdit, setCanEdit] = useState(true);
  useEffect(() => {
    api.getTaskMembers()
      .then(d => { setMembers(d.members || []); setCanEdit(d.can_edit !== false); })
      .catch(() => {});
  }, []);
  const byId = Object.fromEntries(members.map(m => [m.user_id, m]));
  return { members, byId, canEdit };
}

export function relativeTime(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}
